import React from "react";
import { Box, Button, Checkbox, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import type { SpecsValidationIssue } from "../../../service/SpecsRevisionService";
import { createStyles, palette } from "../../../styles/theme";
import { SpecsFabricColorCompatibilityModel } from "../specsModel/fabrics/SpecsFabricColorCompatibilityModel";
import { SpecsFabricsModel } from "../specsModel/SpecsFabricsModel";
import {
  getValueAtModelPath,
  setValueAtModelPath,
} from "../specsForm/modelPath";
import { SpecsValidationIssueShell } from "../specsForm/SpecsValidationIssueHighlight";
import { getSpecsIssueMatchForModelPath } from "../specsValidationIssues";
import { CompactTextCell, JsonEditor } from "./FabricsSpecsTableEditorFields";

const styles = createStyles({
  detailsPaper: {
    p: 1.5,
    bgcolor: palette.grey.lightBackground,
    borderRadius: 1,
    border: `1px solid ${palette.grey.lightGrey}`,
  },
  detailsGrid: {
    display: "grid",
    gap: 1.5,
    gridTemplateColumns: {
      xs: "1fr",
      lg: "repeat(3, minmax(0, 1fr))",
    },
  },
  detailsWide: {
    gridColumn: {
      xs: "span 1",
      lg: "span 3",
    },
  },
  compatibilityStack: {
    pt: 0.5,
  },
});

export function FabricColorAdvancedDetails({
  model,
  basePath,
  validationIssues = [],
}: {
  model: SpecsFabricsModel;
  basePath: string;
  validationIssues?: SpecsValidationIssue[];
}) {
  const compatibility = getValueAtModelPath(
    model,
    `${basePath}.colorCompatibility`
  );
  const getIssueMatch = (modelPath: string, includeAncestorTargets = false) =>
    getSpecsIssueMatchForModelPath({
      issues: validationIssues,
      specType: "fabric",
      modelPath,
      includeAncestorTargets,
    });

  return (
    <Box sx={styles.detailsPaper}>
      <Box sx={styles.detailsGrid}>
        <SpecsValidationIssueShell
          match={getIssueMatch(`${basePath}.descriptionOverwrite`)}
        >
          <Typography variant="caption" color="text.secondary">
            Description overwrite
          </Typography>
          <CompactTextCell
            value={String(
              getValueAtModelPath(model, `${basePath}.descriptionOverwrite`) ??
                ""
            )}
            onChange={(nextValue) =>
              setValueAtModelPath(
                model,
                `${basePath}.descriptionOverwrite`,
                nextValue
              )
            }
            minWidth={200}
          />
        </SpecsValidationIssueShell>

        <SpecsValidationIssueShell
          match={getIssueMatch(`${basePath}.colorCompatibility`, true)}
        >
          <Typography variant="caption" color="text.secondary">
            Compatibility
          </Typography>
          {compatibility ? (
            <Stack spacing={0.5} sx={styles.compatibilityStack}>
              <label>
                <Checkbox
                  size="small"
                  checked={Boolean(
                    getValueAtModelPath(
                      model,
                      `${basePath}.colorCompatibility.colorTendsToBleed`
                    )
                  )}
                  onChange={(_, nextValue) =>
                    setValueAtModelPath(
                      model,
                      `${basePath}.colorCompatibility.colorTendsToBleed`,
                      nextValue
                    )
                  }
                />
                <Typography component="span" variant="caption">
                  Tends to bleed
                </Typography>
              </label>
              <label>
                <Checkbox
                  size="small"
                  checked={Boolean(
                    getValueAtModelPath(
                      model,
                      `${basePath}.colorCompatibility.colorTendsToBeBledOn`
                    )
                  )}
                  onChange={(_, nextValue) =>
                    setValueAtModelPath(
                      model,
                      `${basePath}.colorCompatibility.colorTendsToBeBledOn`,
                      nextValue
                    )
                  }
                />
                <Typography component="span" variant="caption">
                  Tends to be bled on
                </Typography>
              </label>
            </Stack>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() =>
                setValueAtModelPath(
                  model,
                  `${basePath}.colorCompatibility`,
                  new SpecsFabricColorCompatibilityModel()
                )
              }
            >
              Add compatibility
            </Button>
          )}
        </SpecsValidationIssueShell>

        <Box sx={styles.detailsWide}>
          <SpecsValidationIssueShell match={getIssueMatch(`${basePath}.extra`)}>
            <Typography variant="caption" color="text.secondary">
              Extra JSON
            </Typography>
            <JsonEditor
              value={getValueAtModelPath(model, `${basePath}.extra`)}
              onChange={(nextValue) =>
                setValueAtModelPath(model, `${basePath}.extra`, nextValue)
              }
            />
          </SpecsValidationIssueShell>
        </Box>
      </Box>
    </Box>
  );
}
